import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { getDashboardData } from 'helpers/backend_helper';
import CountUp from 'react-countup';
import { GraduationCap, User, Users, BookOpen, CreditCard, DollarSign } from 'lucide-react';
import { useAuth } from "hooks/useStore";



// Define types for our components
type CountBoxProps = {
    title: string;
    count: number;
    color?: string;
    icon?: React.ReactNode;
};


const Ecommerce = () => {

    const navigate = useNavigate();
    const { user } = useAuth();
    
    // Define interface for our dashboard counts data
    interface DashboardCounts {
        totalPrograms: number;
        totalTeachers: number;
        totalClasses: number;
        totalStudents: number;
        feesDueThisMonth: number;
        paymentReceivedThisMonth: number;
        salaryPaidThisMonth: number;
        salaryDueThisMonth: number;
    }

    // Sample data with proper typing - placeholder values matching API response
    const counts: DashboardCounts = {
        totalPrograms: 4,
        totalTeachers: 1,
        totalClasses: 6,
        totalStudents: 12,
        feesDueThisMonth: 0,
        paymentReceivedThisMonth: 0,
        salaryPaidThisMonth: 0,
        salaryDueThisMonth: 0,
    };

    const [dashboardData, setDashboardData] = useState<DashboardCounts>(counts);

    useEffect(() => {
        // Redirect parents to parent dashboard
        if (user?.roleId === 3) {
            navigate("/parent-dashboard");
            return;
        }
        console.log('on dashboard');
        onGetDashboardData();
    }, [user, navigate]);


    const onGetDashboardData = async () => {
        try {
            console.log('Calling GET_DASHBOARD_DATA API...');
            const response = await getDashboardData(user?.userId || 0);
            console.log('Dashboard API Response:', response);
            
            // Handle different response formats
            if (response.data) {
                // If response.data is an array, take the first element
                const data = Array.isArray(response.data) ? response.data[0] : response.data;
                
                // Map the API response to our interface
                const mappedData: DashboardCounts = {
                    totalPrograms: data.totalPrograms || 0,
                    totalTeachers: data.totalTeachers || 0,
                    totalClasses: data.totalClasses || 0,
                    totalStudents: data.totalStudents || 0,
                    feesDueThisMonth: data.feesDueThisMonth || 0,
                    paymentReceivedThisMonth: data.paymentReceivedThisMonth || 0,
                    salaryPaidThisMonth: data.salaryPaidThisMonth || 0,
                    salaryDueThisMonth: data.salaryDueThisMonth || 0,
                };
                
                console.log('Mapped dashboard data:', mappedData);
                setDashboardData(mappedData);
            } else {
                console.warn('No data in API response, using placeholder data');
                setDashboardData(counts);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // Use placeholder data if API fails
            setDashboardData(counts);
        }
    }






    // Reusable Count Box Component with TypeScript
    const CountBox: React.FC<CountBoxProps> = ({ title, count, color, icon }) => {
        return (
            <div className="2xl:col-span-3 2xl:row-span-1 md:col-span-2 md:row-span-1">
                <div className="card">
                    <div className="flex items-center gap-3 card-body">
                        <div className={`flex items-center justify-center size-12 rounded-md text-15 bg-${color}-100 text-${color}-500 dark:bg-custom-500/20 shrink-0`}>
                            {icon}
                        </div>
                        <div className="grow">
                            <h5 className="mb-1 text-16">
                                <CountUp end={count || 0} separator="," className="counter-value" />
                            </h5>
                            <p className="text-slate-500 dark:text-zink-200">{title}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };



    return (
        <React.Fragment>
            <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
                <div className="flex flex-col gap-2 py-4 md:py-6">
                    <div>
                    <div className="grid grid-cols-1 gap-x-5 md:grid-cols-12 2xl:grid-cols-12">
                        <CountBox
                            title="Total Programs"
                            count={dashboardData.totalPrograms}
                            color="blue"
                            icon={<GraduationCap className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Total Teachers"
                            count={dashboardData.totalTeachers}
                            color="green"
                            icon={<User className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Total Classes"
                            count={dashboardData.totalClasses}
                            color="purple"
                            icon={<BookOpen className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Total Students"
                            count={dashboardData.totalStudents}
                            color="indigo"
                            icon={<Users className="w-6 h-6" />}
                        />
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-x-5 md:grid-cols-12 2xl:grid-cols-12">
                        <CountBox
                            title="Fees Due This Month"
                            count={dashboardData.feesDueThisMonth}
                            color="red"
                            icon={<CreditCard className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Payment Received This Month"
                            count={dashboardData.paymentReceivedThisMonth}
                            color="green"
                            icon={<CreditCard className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Salary Paid This Month"
                            count={dashboardData.salaryPaidThisMonth}
                            color="green"
                            icon={<DollarSign className="w-6 h-6" />}
                        />

                        <CountBox
                            title="Salary Due This Month"
                            count={dashboardData.salaryDueThisMonth}
                            color="red"
                            icon={<DollarSign className="w-6 h-6" />}
                        />
                    </div>

                    </div>
                </div>
            </div>

        </React.Fragment>
    );
};

export default Ecommerce;
